import { Box, Stack } from "@mui/material";
import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { InfiniteScroll } from "@smartrr/shared/components/InfiniteScroll";
import { ItemSkeleton } from "@smartrr/shared/components/InfiniteScroll/components/ItemSkeleton";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { TitleAndSearchBar } from "@smartrr/shared/customerPortal/components/TitleAndSearchBar";
import { ICancellationReason } from "@smartrr/shared/entities/CancellationRelationship";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { extractShopifyId, shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { getPurchasableFromPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { smartrrAccountConfig } from "@customer-app/account/config";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { getPlan } from "@customer-app/account/views/OperationAquarius/utils/Pricing/Delivery";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";
import {
  VariantPurchaseOptions,
  useProductsListForSellingPlanId,
  useVariantEnablementForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";

import { getFirstAvailableLineItemForSelection } from "./libs/utils";
import { useGetAllSellingPlanGroups } from "../../../../../hooks/useGetAllSellingPlanGroups";
import { usePurchasablesForSubscribing } from "../../../../../hooks/usePurchasablesForSubscribing";
import {
  useSubscriptionActionStates,
  useSubscriptionActions,
} from "../../../../../store/SubscriptionActionStore";
import { NoProducts } from "../../../AddonList/libs/components/NoProducts";
import { VerticalProductWrapper } from "../../../constants";
import { Item } from "../../../Item";
import { ModalProductList } from "../ModalProductList";
import { VariantSelector } from "../Selectors/libs/components/VariantSelector";
import { useBasicSubscriptionInfo } from "../../ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

interface ISwapItemViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  setIconButtonInactive: () => void;
  saveUserCancellationData?: (cancel?: boolean) => void;
  cancellationReason?: ICancellationReason | undefined;

  /** If provided, the line item with a matching id will only be shown within ModalProductList (Swap Out) */
  initialLineItemId?: string;
}

export const SwapItemView = ({
  delivery,
  setIconButtonInactive,
  saveUserCancellationData,
  cancellationReason,
  initialLineItemId = "",
}: ISwapItemViewProps): JSX.Element => {
  const { isLoading } = useSubscriptionActionStates();
  const { onSwapItem } = useSubscriptionActions();
  const closeModal = useCloseModal();
  const isMobile = useMobileQuery();
  const { purchaseState, sellingPlanId, isPrepaid, isSellingPlanDeleted } = useBasicSubscriptionInfo(delivery);
  const { currency, stLineItems } = purchaseState;

  const customerCountryCode = useSmartrrCustomerSelector(
    state => state.auth.customerRelationship?.defaultAddress?.countryCode
  );

  const innerRef = useRef<HTMLDivElement>(null);
  const innerRefHeightString = `${innerRef.current?.clientHeight}px !important`;

  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const handleError = useErrorHandler();
  const theme = useSmartrrTheme();

  // SwapIn / SwapOut Values
  const [swapOutActivePurchaseable, setSwapOutActivePurchaseable] = useState<IPurchasable | null>(null);
  const [swapInActivePurchaseable, setSwapInActivePurchaseable] = useState<IPurchasable | null>(null);
  const [swapOutActiveItem, setSwapOutActiveItem] = useState<IPurchaseStateLineItem | null>(null);
  const [swapInActiveItem, setSwapInActiveItem] = useState<IPurchaseStateLineItem | null>(null);

  const [swapInItemsToShow, setSwapInItemsToShow] = useState<IPurchasable[]>([]);

  // SearchBar
  const [subscribedPurchasablesSearchText, updateSubscribedPurchasablesSearchText] = useState<string>("");

  const [selectedVariantOption, setSelectedVariantOption] = useState<VariantPurchaseOptions | undefined>();

  const { hiddenProducts } = smartrrAccountConfig;

  const activeQty = useRef<number | null>(null);

  const subscribablePurchasables = useProductsListForSellingPlanId(sellingPlanId!, false);
  const getPurchasablesForSubscribing = usePurchasablesForSubscribing(delivery);
  const purchasablesForSwapping = useMemo(() => {
    const purchaseables = getPurchasablesForSubscribing(delivery, subscribablePurchasables);

    if (subscribedPurchasablesSearchText) {
      return purchaseables.filter(p =>
        p.purchasableName.toLocaleLowerCase().includes(subscribedPurchasablesSearchText.toLocaleLowerCase())
      );
    }
    return purchaseables;
  }, [delivery, subscribablePurchasables, subscribedPurchasablesSearchText]);

  let filteredSubscribedPurchasables: IPurchasable[] = purchasablesForSwapping;

  if (hiddenProducts?.length) {
    filteredSubscribedPurchasables = filteredSubscribedPurchasables.filter(purchasablesForSwapping => {
      const shopifyId = purchasablesForSwapping.shopifyId
        ? shopifyGidToNumber(purchasablesForSwapping.shopifyId).toString()
        : "";
      return !hiddenProducts.includes(shopifyId);
    });
  }

  const oosLineItem = useMemo(
    () => delivery.purchaseState.stLineItems.find(li => li.id === initialLineItemId),
    [delivery.purchaseState.stLineItems.length, initialLineItemId]
  );

  // if only one product (filtering out addons), set active
  useEffect(() => {
    if (!swapOutActiveItem) {
      const item =
        oosLineItem ?? getFirstAvailableLineItemForSelection(stLineItems, delivery.indexFromScheduleStart);
      if (item) {
        setSwapOutActiveItem(item);
        setSwapOutActivePurchaseable(getPurchasableFromPurchasableMap(variantToPurchasableMap, item.vnt));
      }
    }
  }, [delivery, purchaseState, stLineItems, swapOutActiveItem, variantToPurchasableMap]);

  const groups = useGetAllSellingPlanGroups();
  const selectedPlan = getPlan(groups, sellingPlanId);

  useEffect(() => {
    setSwapInItemsToShow(filteredSubscribedPurchasables.slice(0, 10));
  }, [filteredSubscribedPurchasables, purchasablesForSwapping, subscribedPurchasablesSearchText]);

  const enabledVariantOptions = useVariantEnablementForSellingPlanId(
    swapInActivePurchaseable!,
    sellingPlanId,
    false
  );

  const onSwapHandler = useCallback(
    (
      variant: IPurchasableVariant,
      quantity: number,
      sellingPlanId: string | undefined | null,
      previousItemId: string,
      swapAsAddon: boolean
    ) => {
      if (quantity === 0) {
        toast.error(typedLocalize("subscriptions.invalidQuantityToast"));
        return;
      }

      if (sellingPlanId === null || sellingPlanId === undefined) {
        handleError(new Error(`Error: Invalid selling plan id, lineItemId:${previousItemId}`));
        return;
      }

      if (cancellationReason && saveUserCancellationData) {
        saveUserCancellationData();
      }

      return onSwapItem(
        variant,
        quantity,
        sellingPlanId,
        previousItemId,
        selectedPlan,
        swapAsAddon,
        customerCountryCode
      ).finally(() => closeModal());
    },
    []
  );

  return (
    <React.Fragment>
      <Box ref={innerRef} sx={{ marginTop: "15px" }} className={prefixClassName("swap-container")}>
        {isLoading ? (
          <Stack justifyContent="center" alignItems="center" height={innerRefHeightString}>
            <DotSpinner />
          </Stack>
        ) : (
          <React.Fragment>
            <ModalProductList
              delivery={delivery}
              activeQty={activeQty}
              title={
                initialLineItemId
                  ? typedLocalize(
                      "subscriptions.yourSubscriptions.orderOptionsModal.swapItemView.singleSwapTitle"
                    )
                  : typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.swapItemView.swapOutTitle")
              }
              activePurchaseable={swapOutActivePurchaseable}
              setActivePurchaseable={setSwapOutActivePurchaseable}
              activeItem={swapOutActiveItem!}
              setActiveItem={setSwapOutActiveItem!}
              showQtySelector={false}
              showPrice={false}
              initialLineItemId={initialLineItemId}
              enabledVariantOptions={enabledVariantOptions}
              isPrepaid={isPrepaid}
              isSellingPlanDeleted={isSellingPlanDeleted}
              hideBundleItems
              hideAddons
              selectOnClick
              disableWrap
              showVariant
              showUnavailableTag
            />
            {swapOutActiveItem ? (
              <Box className={prefixClassName("swap-out-wrapper")}>
                <hr />
                <TitleAndSearchBar
                  sx={{ marginTop: "15px" }}
                  title={typedLocalize(
                    "subscriptions.yourSubscriptions.orderOptionsModal.swapItemView.swapInTitle"
                  )}
                  isMobile={isMobile}
                  subscribedPurchasablesSearchText={subscribedPurchasablesSearchText}
                  updateSubscribedPurchasablesSearchText={updateSubscribedPurchasablesSearchText}
                />
                {(swapInItemsToShow.length > 0 && (
                  <Box>
                    <Box
                      sx={{
                        overflowX: "scroll",
                        height: "max-content",
                        overflowY: "hidden",
                        marginTop: "15px",
                      }}
                    >
                      <InfiniteScroll
                        isParentLoading={isLoading}
                        hasMore={purchasablesForSwapping.length > swapInItemsToShow.length}
                        skeleton={<ItemSkeleton radius={theme.modernImageBorderRadius} />}
                        horizontal={true}
                        handleAddItemsToScroll={numberOfItemsToLoad =>
                          setSwapInItemsToShow(prevItems => [
                            ...prevItems,
                            ...purchasablesForSwapping.slice(swapInItemsToShow.length, numberOfItemsToLoad),
                          ])
                        }
                      >
                        {swapInItemsToShow.map((prod: IPurchasable) => {
                          const productId = extractShopifyId(prod.shopifyId);
                          const variantFromDropdown =
                            prod.id === swapInActivePurchaseable?.id
                              ? swapInActivePurchaseable?.vnts?.find(
                                  vnt => vnt.id === selectedVariantOption?.vnt.id
                                )
                              : null;
                          if (!prod.isDraftOrArchived) {
                            return (
                              <VerticalProductWrapper
                                item
                                key={prod.id}
                                className={
                                  prod.id === swapInActivePurchaseable?.id
                                    ? prefixClassName("addon-selected")
                                    : prefixClassName("addon")
                                }
                                data-smartrr-shopify-id={productId}
                              >
                                <Item
                                  delivery={delivery}
                                  setActivePurchaseable={setSwapInActivePurchaseable!}
                                  activePurchaseable={swapInActivePurchaseable!}
                                  setSelectedVariantOption={setSelectedVariantOption}
                                  setItem={setSwapInActiveItem}
                                  item={swapInActiveItem!}
                                  activeQty={activeQty}
                                  product={prod}
                                  variantFromDropdown={variantFromDropdown}
                                  selectOnClick
                                  showQtySelector={!isPrepaid}
                                />
                              </VerticalProductWrapper>
                            );
                          }
                          return <React.Fragment key={prod.id} />;
                        })}
                      </InfiniteScroll>
                    </Box>
                    {swapInActivePurchaseable ? (
                      <VariantSelector
                        purchasable={swapInActivePurchaseable!}
                        isLoading={isLoading}
                        enabledVariantOptions={enabledVariantOptions}
                        currency={currency}
                        selectedVariantOption={selectedVariantOption}
                        setSelectedVariantOption={setSelectedVariantOption}
                      />
                    ) : null}
                  </Box>
                )) || <NoProducts height={"196px !important"} />}
              </Box>
            ) : null}
          </React.Fragment>
        )}
      </Box>
      <ConfirmCancelButtons
        onCancel={setIconButtonInactive}
        onConfirm={async () => {
          const qty = (isPrepaid ? swapOutActiveItem?.quantity : activeQty.current!) ?? activeQty.current!;
          return await onSwapHandler(
            selectedVariantOption?.vnt!,
            qty,
            sellingPlanId,
            swapOutActiveItem!.id,
            swapOutActiveItem?.isAddOn ?? false
          );
        }}
        isLoading={isLoading}
        showConfirmButton={!!(swapOutActivePurchaseable && swapInActivePurchaseable)}
      />
    </React.Fragment>
  );
};
